import { Link as LinkIcon, User, CheckCircle2 } from 'lucide-react'

import { Avatar, AvatarFallback, AvatarImage } from '@/common/ui/avatar'
import { Dialog, DialogContent } from '@/common/ui/dialog'
import { Separator } from '@/common/ui/separator'

import type { components } from '@/schema'

type PlayerResponse = components['schemas']['PlayerResponse']

interface ProfileActionDialogProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	displayName: string
	pictureUrl?: string
	player?: PlayerResponse | null
	onLinkPlayer: () => void
	onConnectLine?: () => void
	isConnectLineLoading?: boolean
	lineUserId?: string
}

export function ProfileActionDialog({
	open,
	onOpenChange,
	displayName,
	pictureUrl,
	player,
	onLinkPlayer,
	onConnectLine,
	isConnectLineLoading,
	lineUserId,
}: ProfileActionDialogProps) {
	// Check if LINE is already connected
	const isLineConnected =
		player?.lineId && lineUserId && player.lineId === lineUserId
	// Show connect LINE button when player exists and lineUserId exists
	const showConnectLine = player && lineUserId

	const handleGoToPlayerDetail = () => {
		if (player) {
			window.open(
				`/operator/get-player-list/player-detail/${player.id}`,
				'_blank',
			)
			onOpenChange(false)
		}
	}

	const handleLinkPlayer = () => {
		onLinkPlayer()
		onOpenChange(false)
	}

	const handleConnectLine = () => {
		if (onConnectLine && !isLineConnected) {
			onConnectLine()
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-xs gap-0 overflow-hidden p-0">
				{/* Profile Header with gradient background */}
				<div className="relative bg-gradient-to-br from-teal-600 to-teal-800 pb-16 pt-8">
					{/* Avatar */}
					<div className="absolute -bottom-12 left-1/2 -translate-x-1/2">
						<Avatar className="h-24 w-24 border-4 border-background shadow-lg">
							<AvatarImage src={pictureUrl} alt={displayName} />
							<AvatarFallback className="bg-muted text-2xl">
								<User className="h-12 w-12 text-muted-foreground" />
							</AvatarFallback>
						</Avatar>
					</div>
				</div>

				{/* Profile Info */}
				<div className="px-6 pb-4 pt-14 text-center">
					<div className="flex items-center justify-center gap-2">
						<h2 className="text-xl font-semibold">{displayName}</h2>
					</div>
					{isLineConnected && (
						<div className="mt-1 flex items-center justify-center gap-1 text-xs text-green-500">
							<CheckCircle2 className="h-4 w-4" />
							<span>เชื่อมต่อ LINE แล้ว</span>
						</div>
					)}
					{player && (
						<p className="mt-1 text-sm text-muted-foreground">
							{player.username || player.phone}
						</p>
					)}
				</div>

				<Separator />

				{/* Action Buttons - LINE style */}
				<div className="flex divide-x">
					{/* Chat / Link Player Button */}
					<button
						onClick={player ? handleGoToPlayerDetail : handleLinkPlayer}
						className="flex flex-1 flex-col items-center gap-2 py-4 transition-colors hover:bg-muted"
					>
						{player ? (
							<>
								<User className="h-6 w-6" />
								<span className="text-sm">ข้อมูล</span>
							</>
						) : (
							<>
								<LinkIcon className="h-6 w-6" />
								<span className="text-sm">เชื่อมต่อ</span>
							</>
						)}
					</button>

					{/* Connect LINE Button */}
					{showConnectLine && onConnectLine && (
						<button
							onClick={handleConnectLine}
							disabled={isLineConnected || isConnectLineLoading}
							className="flex flex-1 flex-col items-center gap-2 py-4 transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
						>
							<img
								src="https://cdn-icons-png.flaticon.com/512/3128/3128218.png"
								alt="LINE"
								className="w-6"
							/>

							<span className="text-sm">
								{isLineConnected ? 'เชื่อมต่อแล้ว' : 'เชื่อมต่อ LINE'}
							</span>
						</button>
					)}

					{/* Manage Link Button - only show if player is linked */}
					{player && (
						<button
							onClick={handleLinkPlayer}
							className="flex flex-1 flex-col items-center gap-2 py-4 transition-colors hover:bg-muted"
						>
							<LinkIcon className="h-6 w-6" />
							<span className="text-sm">จัดการ</span>
						</button>
					)}
				</div>
			</DialogContent>
		</Dialog>
	)
}
